import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    VIEW, DELETE,
    IEC_61508_2010,
    IEC_61508_1998,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,

    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
} from "const"
import { SilSafetyFunction } from "server/models"
import {
    PRM_SIL_SAFETY_FUNCTION,
    StoreType,
    addModal,
} from "client/store"
import { hasProjectAccess, SilCalculationMethod } from "client/utils"
import {
    Link,
    DateFormatter,
    TableColumn,
    Empty,
} from "client/components"

import FormatProbability from "client/pages/sil/format/probability"
import { getMaxProbability } from "client/pages/sil/formulas/internal"

const Columns = () => {
    const baseRoute = useSelector((state: StoreType) => state.silSafetyFunction.baseRoute)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const deleteAccess = hasProjectAccess(PRM_SIL_SAFETY_FUNCTION, DELETE)

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (safetyFunction: SilSafetyFunction) => {
                const href = `${baseRoute}/${VIEW}/${safetyFunction.id}`
                return (
                    <Link
                        href={href}
                        onClick={() => dispatch(addModal({ url: href }))}
                    >
                        {safetyFunction.name}
                    </Link>
                )
            },
        },
        {
            field: "calculationMethod",
            header: lang.calculationMethod,
            sortable: true,
            cellMinWidth: "200px",
            formatter: (safetyFunction: SilSafetyFunction) => {
                const { calculationMethodDangerous, calculationMethodSafe } = safetyFunction

                // calculation method can be or dangerous or safe or "no dangerous/safe failure", never both
                let calculationMethod: SilCalculationMethod = null
                if (calculationMethodDangerous !== NO_DANGEROUS_FAILURE) calculationMethod = calculationMethodDangerous
                if (calculationMethodSafe !== NO_SAFE_FAILURE) calculationMethod = calculationMethodSafe

                let calculationMethodString: string
                if (calculationMethod === IEC_61508_2010) calculationMethodString = lang.iec_61508_2010
                if (calculationMethod === IEC_61508_1998) calculationMethodString = lang.iec_61508_1998
                if (calculationMethod === ISA_TR84_2002) calculationMethodString = lang.isa_tr84_2002
                if (calculationMethod === ISA_TR84_2022) calculationMethodString = lang.isa_tr84_2022
                if (calculationMethod === VDI_VDE_2180_2019) calculationMethodString = lang.vdi_vde_2180_2019

                return calculationMethodString
            },
        },
        {
            field: "mode",
            header: lang.mode,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (safetyFunction: SilSafetyFunction) => {
                let mode: string
                if (safetyFunction.mode === LOW_DEMAND_MODE) mode = lang.modeLowDemand
                if (safetyFunction.mode === HIGH_DEMAND_MODE) mode = lang.modeHighDemand
                if (safetyFunction.mode === CONTINUOUS_MODE) mode = lang.modeContinuous

                return mode
            },
        },
        {
            field: "targetSil",
            header: lang.targetSil,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (safetyFunction: SilSafetyFunction) => (
                safetyFunction.targetSil ? safetyFunction.targetSil : <Empty />
            ),
        },
        {
            field: "targetPfd",
            header: lang.targetPfd,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (safetyFunction: SilSafetyFunction) => (
                <FormatProbability
                    data={
                        // show "maxProbability" as default value if "targetPfd" is empty
                        safetyFunction.targetPfd !== null
                            ? safetyFunction.targetPfd
                            : getMaxProbability(safetyFunction.mode, safetyFunction.targetSil)
                    }
                    tableView
                />
            ),
        },
        {
            field: "calculated",
            header: lang.calculated,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (safetyFunction: SilSafetyFunction) => (safetyFunction.calculated ? lang.yes : lang.no),
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            formatter: (safetyFunction: SilSafetyFunction) => <DateFormatter date={safetyFunction.createdAt} />,
        },
    ]

    if (deleteAccess) {
        columns.push({
            field: "delete",
            centerAlign: true,
            deleteAccess: () => true,
        })
    }

    return columns
}
export default Columns
